/**
 * Reading the situation out of a URL.
 *
 * A link is untrusted input, and several of these values are handed straight to
 * domain functions that throw on nonsense — `resolve()` rejects an unknown
 * phase, `zoneOf()` rejects a rotation outside 1-6, `t()` rejects a missing
 * locale. A throw during boot aborts module evaluation and leaves a black page,
 * so every value is checked here and falls back to something sensible.
 */

import { positions } from "../../coaching/src/content.js";
import { LOCALES, STRINGS } from "../../coaching/src/i18n.js";
import { PHASES } from "../../domain/src/phases.js";

const POSITION_IDS = positions().map((position) => position.id);
/** Camera names come from the string table, so an accepted one always has a label. */
const CAMERA_IDS = Object.keys(STRINGS.en.cameras);

/** A fast-forward long enough to reach any moment of a rally, and no longer. */
const MAX_FAST_FORWARD = 30;

const oneOf = <T extends string, F extends T | null>(value: string | null, allowed: readonly T[], fallback: F): T | F =>
  allowed.includes(value as T) ? value as T : fallback;

export function parseDeepLink(params: URLSearchParams) {
  const askedPosition = params.get("position");
  const position = oneOf(askedPosition, POSITION_IDS, null);

  const askedRotation = Number(params.get("rotation"));
  const rotation =
    Number.isInteger(askedRotation) && askedRotation >= 1 && askedRotation <= 6
      ? askedRotation
      : 1;

  const askedAt = Number(params.get("at"));
  const at =
    Number.isFinite(askedAt) && askedAt > 0
      ? Math.min(askedAt, MAX_FAST_FORWARD)
      : 0;

  return {
    /** Naming a real position is what opens the court instead of the title. */
    deepLink: position !== null,
    position: position ?? "OH",
    rotation,
    weServe: params.get("serve") === "us",
    phase: oneOf(params.get("phase"), [...PHASES, "defence" as const], "base"),
    camera: oneOf(params.get("camera"), CAMERA_IDS, "broadcast"),
    locale: oneOf(params.get("lang"), LOCALES, null),
    // Opt out of the libero with ?libero=0; on by default, as most teams are.
    libero: params.get("libero") !== "0",
    guide: params.get("guide") === "1",
    play: params.get("play") === "1",
    at,
  };
}
